import React, { useState, useEffect, useMemo, useCallback, useRef, memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  Box,
  Divider,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  Class as ClassIcon,
  Subject as SubjectIcon,
  Quiz as QuizIcon,
  Grade as GradeIcon,
  CheckCircle as CheckCircleIcon,
  Announcement as AnnouncementIcon,
  WorkOff as WorkOffIcon,
  ExpandLess,
  ExpandMore,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as TeacherIcon,
  FamilyRestroom as ParentIcon,
  Group as StudentsIcon,
  EmojiPeople as AlumniIcon,
  Settings as SettingsIcon,
  Schedule as ScheduleIcon,
  Category as CategoryIcon,
  AccountBalanceWallet as FeeIcon,
  Assignment as ClassFeeIcon,
  PersonAdd as StudentFeeIcon,
  ReceiptLong as TransactionIcon,
  Assessment as FeeStatusIcon,
  TrendingUp as PromotionIcon,
  Message as MessageIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useAppConfig } from '../../contexts/AppConfigContext';

// Menu items for admin
const getMenuItems = () => {
  return [
    {
      title: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
    },
    {
      title: 'User Management',
      icon: <PeopleIcon />,
      children: [
        {
          title: 'Students',
          icon: <StudentsIcon />,
          path: '/users/students',
        },
        {
          title: 'Alumni',
          icon: <AlumniIcon />,
          path: '/users/alumni',
        },
        {
          title: 'Teachers',
          icon: <TeacherIcon />,
          path: '/users/teachers',
        },
        {
          title: 'Parents',
          icon: <ParentIcon />,
          path: '/users/parents',
        },
      ],
    },
    {
      title: 'Classes & Subjects',
      icon: <SchoolIcon />,
      children: [
        {
          title: 'Classes',
          icon: <ClassIcon />,
          path: '/classes',
        },
        {
          title: 'Subjects',
          icon: <SubjectIcon />,
          path: '/subjects',
        },
      ],
    },
    {
      title: 'Exams & Results',
      icon: <AssignmentIcon />,
      children: [
        {
          title: 'Exams',
          icon: <QuizIcon />,
          path: '/exams',
        },
        {
          title: 'Results',
          icon: <GradeIcon />,
          path: '/results',
        },
      ],
    },
    {
      title: 'Fee Management',
      icon: <FeeIcon />,
      children: [
        {
          title: 'Fee Categories',
          icon: <CategoryIcon />,
          path: '/fees/categories',
        },
        {
          title: 'Assign Class Fees',
          icon: <ClassFeeIcon />,
          path: '/fees/classes',
        },
        {
          title: 'Assign Student Fees',
          icon: <StudentFeeIcon />,
          path: '/fees/students',
        },
        {
          title: 'Transactions',
          icon: <TransactionIcon />,
          path: '/fees/transactions',
        },
        {
          title: 'Fee Status',
          icon: <FeeStatusIcon />,
          path: '/fees/status',
        },
      ],
    },
    {
      title: 'Routine',
      icon: <ScheduleIcon />,
      path: '/routine',
    },
    {
      title: 'Feedback',
      icon: <MessageIcon />,
      path: '/feedback',
    },
    {
      title: 'Attendance',
      icon: <CheckCircleIcon />,
      path: '/attendance',
    },
    {
      title: 'Announcements',
      icon: <AnnouncementIcon />,
      path: '/announcements',
    },
    {
      title: 'Leave Requests',
      icon: <WorkOffIcon />,
      path: '/leave',
    },
    {
      title: 'Setup',
      icon: <AdminIcon />,
      path: '/setup',
    },
    {
      title: 'Promotion',
      icon: <PromotionIcon />,
      path: '/promotion',
    },
    {
      title: 'Settings',
      icon: <SettingsIcon />,
      path: '/settings',
    },
  ];
};

const Sidebar = ({ open, onClose, drawerWidth }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { isTeacher, isParent } = useAuth();
  const { schoolName, feeManagementEnabled, promotionEnabled } = useAppConfig();

  // Get pathname and track it in ref to minimize re-renders
  const pathnameRef = useRef(location.pathname);
  const [pathnameVersion, setPathnameVersion] = useState(0);

  // Only update version when pathname actually changes - this prevents unnecessary recalculations
  useEffect(() => {
    if (pathnameRef.current !== location.pathname) {
      pathnameRef.current = location.pathname;
      setPathnameVersion(prev => prev + 1);
    }
  }, [location.pathname]);

  // Use refs to store stable callbacks
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Get user role flags once to avoid function calls in dependencies
  const userIsTeacher = useMemo(() => isTeacher(), [isTeacher]);
  const userIsParent = useMemo(() => isParent(), [isParent]);

  // Filter menu items based on user role and fee management config
  const filteredMenuItems = useMemo(() => {
    if (userIsTeacher) {
      // Teacher menu items
      return [
        {
          title: 'Dashboard',
          icon: <DashboardIcon />,
          path: '/teacher-dashboard',
        },
        {
          title: 'My Subjects & Class',
          icon: <SubjectIcon />,
          path: '/subjects',
        },
        {
          title: 'Attendance',
          icon: <CheckCircleIcon />,
          path: '/attendance',
        },
        {
          title: 'Results',
          icon: <GradeIcon />,
          path: '/results',
        },
        {
          title: 'Feedback',
          icon: <MessageIcon />,
          path: '/feedback',
        },
        {
          title: 'Fee Status',
          icon: <FeeStatusIcon />,
          path: '/fees/status',
        },
        {
          title: 'Leave Requests',
          icon: <WorkOffIcon />,
          path: '/leave',
        },
        {
          title: 'Settings',
          icon: <SettingsIcon />,
          path: '/settings',
        },
      ];
    }
    if (userIsParent) {
      // Parent menu - return empty as they use a different dashboard
      return [];
    }
    // Admin menu - filter out Fee Management and Promotion if disabled
    const menuItems = getMenuItems();
    let filteredItems = menuItems;

    if (!feeManagementEnabled) {
      filteredItems = filteredItems.filter(item => item.title !== 'Fee Management');
    }

    if (!promotionEnabled) {
      filteredItems = filteredItems.filter(item => item.title !== 'Promotion');
    }

    return filteredItems;
  }, [userIsTeacher, userIsParent, feeManagementEnabled, promotionEnabled]);

  const [expandedItems, setExpandedItems] = useState({});

  // Update expanded items when pathname or menu items change (including nested children)
  useEffect(() => {
    const currentPath = pathnameRef.current;
    const newExpanded = {};
    filteredMenuItems.forEach(item => {
      if (item.children) {
        const hasActiveChild = item.children.some(child => {
          if (child.path) {
            // For student detail page, check if path starts with /users/students
            if (child.path === '/users/students' && currentPath.startsWith('/users/students')) {
              return true;
            }
            // For teacher detail page, check if path starts with /users/teachers
            if (child.path === '/users/teachers' && currentPath.startsWith('/users/teachers')) {
              return true;
            }
            // For parent detail page, check if path starts with /users/parents
            if (child.path === '/users/parents' && currentPath.startsWith('/users/parents')) {
              return true;
            }
            return currentPath === child.path;
          }
          if (child.children) {
            const hasNestedActive = child.children.some(nestedChild => currentPath === nestedChild.path);
            if (hasNestedActive) {
              newExpanded[`${item.title}-${child.title}`] = true;
            }
            return hasNestedActive;
          }
          return false;
        });
        if (hasActiveChild) {
          newExpanded[item.title] = true;
        }
      }
    });
    setExpandedItems(prev => {
      // Only update if the expanded state actually changed
      const prevStr = JSON.stringify(prev);
      const newStr = JSON.stringify(newExpanded);
      if (prevStr !== newStr) {
        return newExpanded;
      }
      return prev;
    });
  }, [filteredMenuItems, pathnameVersion]);

  const handleItemClick = useCallback((item, event) => {
    if (item.children) {
      setExpandedItems((prev) => ({
        ...prev,
        [item.title]: !prev[item.title],
      }));
      return;
    }
    if (item.path) {
      navigate(item.path);
      // Close drawer on mobile after navigation
      if (isMobile && onCloseRef.current) {
        onCloseRef.current();
      }
    }
  }, [navigate, isMobile]);

  const handleSubItemClick = useCallback((path, event) => {
    navigate(path);
    // Close drawer on mobile after navigation
    if (isMobile && onCloseRef.current) {
      onCloseRef.current();
    }
  }, [navigate, isMobile]);

  const handleToggleExpand = useCallback((itemTitle) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemTitle]: !prev[itemTitle],
    }));
  }, []);

  const handleToggleNestedExpand = useCallback((key) => {
    setExpandedItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  // Helper functions for active state checks - use pathnameRef to avoid re-creating on every render
  const checkItemActive = useCallback((item, currentPath) => {
    if (item.path) {
      // Handle /subjects path - should be active for /subjects and /subjects/class/:classId
      if (item.path === '/subjects' && (currentPath === '/subjects' || currentPath.startsWith('/subjects/class'))) {
        return true;
      }
      return currentPath === item.path;
    }
    if (item.children) {
      return item.children.some(child => {
        if (child.path) {
          if (child.path === '/users/students' && currentPath.startsWith('/users/students')) return true;
          if (child.path === '/users/teachers' && currentPath.startsWith('/users/teachers')) return true;
          if (child.path === '/users/alumni' && currentPath.startsWith('/users/alumni')) return true;
          if (child.path === '/users/parents' && currentPath.startsWith('/users/parents')) return true;
          if (child.path === '/subjects' && (currentPath === '/subjects' || currentPath.startsWith('/subjects/class'))) return true;
          return currentPath === child.path;
        }
        if (child.children) {
          return child.children.some(nestedChild => currentPath === nestedChild.path);
        }
        return false;
      });
    }
    return false;
  }, []);

  const checkSubItemActive = useCallback((path, currentPath) => {
    if (currentPath === path) return true;
    if (path === '/users/students' && currentPath.startsWith('/users/students')) return true;
    if (path === '/users/teachers' && currentPath.startsWith('/users/teachers')) return true;
    if (path === '/users/alumni' && currentPath.startsWith('/users/alumni')) return true;
    if (path === '/users/parents' && currentPath.startsWith('/users/parents')) return true;
    if (path === '/subjects' && currentPath.startsWith('/subjects/class')) return true;
    return false;
  }, []);

  // Calculate active states separately - only recalculate when pathname actually changes
  const activeStates = useMemo(() => {
    const currentPath = pathnameRef.current;
    const states = {};
    filteredMenuItems.forEach(item => {
      states[item.title] = checkItemActive(item, currentPath);

      if (item.children) {
        item.children.forEach(child => {
          if (child.path) {
            states[child.path] = checkSubItemActive(child.path, currentPath);
          }
          if (child.children) {
            child.children.forEach(nestedChild => {
              if (nestedChild.path) {
                states[nestedChild.path] = checkSubItemActive(nestedChild.path, currentPath);
              }
            });
          }
        });
      }
    });
    return states;
  }, [filteredMenuItems, pathnameVersion, checkItemActive, checkSubItemActive]);

  // Determine if drawer content should be visible (always true on desktop, depends on open prop on mobile)
  const isDrawerContentVisible = isMobile ? open : true;

  // Render menu items - use pre-calculated active states
  const menuItemsList = useMemo(() => {
    return filteredMenuItems.map((item, index) => {
      const isActive = activeStates[item.title] || false;
      const isExpanded = expandedItems[item.title];

      return (
        <React.Fragment key={`${item.title}-${index}`}>
          <ListItem disablePadding>
            <ListItemButton
              onClick={(e) => handleItemClick(item, e)}
              sx={{
                minHeight: 56,
                justifyContent: isDrawerContentVisible ? 'initial' : 'center',
                px: 0.5,
                mx: 2,
                my: 0.75,
                borderRadius: 2,
                backgroundColor: isActive ? 'grey.300' : 'transparent',
                borderLeft: isActive ? '4px solid' : '4px solid transparent',
                borderLeftColor: isActive ? 'primary.main' : 'transparent',
                color: isActive ? 'primary.main' : 'text.primary',
                fontWeight: isActive ? 700 : 600,
                position: 'relative',
                overflow: 'hidden',
                '& .MuiListItemText-primary': {
                  fontSize: '0.95rem',
                },
                '&:hover': {
                  color: 'primary.main',
                },
                '& .MuiListItemIcon-root': {
                  color: isActive ? 'primary.main' : 'text.secondary',
                  minWidth: 40,
                  '& svg': {
                    fontSize: 24,
                  },
                },
                '&:hover .MuiListItemIcon-root': {
                  color: 'primary.main',
                },
                '&:hover .MuiListItemText-primary': {
                  color: 'primary.main',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: isDrawerContentVisible ? 2.5 : 'auto',
                  justifyContent: 'center',
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                {item.icon}
              </ListItemIcon>
              {isDrawerContentVisible && (
                <>
                  <ListItemText
                    primary={item.title}
                    primaryTypographyProps={{
                      variant: 'body1',
                      fontWeight: isActive ? 700 : 500,
                      fontSize: '0.9375rem',
                      letterSpacing: '0.01em',
                    }}
                  />
                  {item.children && (
                    <Box
                      sx={{
                        color: isActive ? 'primary.main' : 'text.secondary',
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      }}
                    >
                      {isExpanded ? <ExpandLess /> : <ExpandMore />}
                    </Box>
                  )}
                </>
              )}
            </ListItemButton>
          </ListItem>

          {item.children && isDrawerContentVisible && (
            <Collapse
              in={isExpanded}
              timeout="auto"
              unmountOnExit
              sx={{
                '& .MuiCollapse-wrapper': {
                  paddingLeft: 2,
                },
              }}
            >
              <List component="div" disablePadding sx={{ pt: 0.5, pb: 0.5, ml: 0.5 }}>
                {item.children.map((subItem, subIndex) => {
                  const hasNestedChildren = subItem.children && subItem.children.length > 0;
                  const nestedExpanded = expandedItems[`${item.title}-${subItem.title}`];
                  const isNestedActive = hasNestedChildren && subItem.children.some(nestedChild => activeStates[nestedChild.path] || false);
                  const isSubActive = subItem.path ? (activeStates[subItem.path] || false) : false;

                  return (
                    <React.Fragment key={`${subItem.title}-${subIndex}`}>
                      <ListItem disablePadding>
                        <ListItemButton
                          onClick={(e) => {
                            e.stopPropagation();
                            if (hasNestedChildren) {
                              handleToggleNestedExpand(`${item.title}-${subItem.title}`);
                            } else if (subItem.path) {
                              handleSubItemClick(subItem.path, e);
                            }
                          }}
                          sx={{
                            pl: 6,
                            minHeight: 44,
                            mx: 2,
                            my: 0.5,
                            borderRadius: 2,
                            backgroundColor: isNestedActive || isSubActive ? 'grey.300' : 'transparent',
                            color: isNestedActive || isSubActive ? 'primary.main' : 'text.secondary',
                            fontWeight: isNestedActive || isSubActive ? 600 : 500,
                            borderLeft: isNestedActive || isSubActive ? '3px solid' : '3px solid transparent',
                            borderLeftColor: isNestedActive || isSubActive ? 'primary.main' : 'transparent',
                            '& .MuiListItemText-primary': {
                              fontSize: '0.9rem',
                            },
                            '&:hover': {
                              color: 'primary.main',
                              '& .MuiListItemIcon-root': {
                                color: 'primary.main',
                              },
                              '& .MuiListItemText-primary': {
                                color: 'primary.main',
                              },
                            },
                            '& .MuiListItemIcon-root': {
                              color: isNestedActive || isSubActive ? 'primary.main' : 'text.secondary',
                              minWidth: 38,
                              '& svg': {
                                fontSize: 20,
                              },
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: 2,
                              justifyContent: 'center',
                            }}
                          >
                            {subItem.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={subItem.title}
                            primaryTypographyProps={{
                              variant: 'body2',
                              fontSize: '0.875rem',
                            }}
                          />
                          {hasNestedChildren && (
                            <Box
                              sx={{
                                color: isNestedActive ? 'primary.main' : 'text.secondary',
                                transform: nestedExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                ml: 1,
                              }}
                            >
                              {nestedExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                            </Box>
                          )}
                        </ListItemButton>
                      </ListItem>
                      {hasNestedChildren && (
                        <Collapse
                          in={nestedExpanded}
                          timeout="auto"
                          unmountOnExit
                          sx={{
                            '& .MuiCollapse-wrapper': {
                              paddingLeft: 2,
                            },
                          }}
                        >
                          <List component="div" disablePadding sx={{ pt: 0.25, pb: 0.25, ml: 0.5 }}>
                            {subItem.children.map((nestedChild, nestedIndex) => {
                              const isNestedSubActive = activeStates[nestedChild.path] || false;
                              return (
                                <ListItem key={`${nestedChild.title}-${nestedIndex}`} disablePadding>
                                  <ListItemButton
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSubItemClick(nestedChild.path, e);
                                    }}
                                    sx={{
                                      pl: 9,
                                      minHeight: 40,
                                      mx: 2,
                                      my: 0.375,
                                      borderRadius: 2,
                                      backgroundColor: isNestedSubActive ? 'grey.300' : 'transparent',
                                      color: isNestedSubActive ? 'primary.main' : 'text.secondary',
                                      fontWeight: isNestedSubActive ? 600 : 500,
                                      borderLeft: isNestedSubActive ? '2px solid' : '2px solid transparent',
                                      borderLeftColor: isNestedSubActive ? 'primary.main' : 'transparent',
                                      '& .MuiListItemText-primary': {
                                        fontSize: '0.85rem',
                                      },
                                      '&:hover': {
                                        color: 'primary.main',
                                        '& .MuiListItemIcon-root': {
                                          color: 'primary.main',
                                        },
                                        '& .MuiListItemText-primary': {
                                          color: 'primary.main',
                                        },
                                      },
                                      '& .MuiListItemIcon-root': {
                                        color: isNestedSubActive ? 'primary.main' : 'text.secondary',
                                        minWidth: 34,
                                        '& svg': {
                                          fontSize: 18,
                                        },
                                      },
                                    }}
                                  >
                                    <ListItemIcon
                                      sx={{
                                        minWidth: 0,
                                        mr: 1.5,
                                        justifyContent: 'center',
                                      }}
                                    >
                                      {nestedChild.icon}
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={nestedChild.title}
                                      primaryTypographyProps={{
                                        variant: 'body2',
                                        fontSize: '0.8125rem',
                                      }}
                                    />
                                  </ListItemButton>
                                </ListItem>
                              );
                            })}
                          </List>
                        </Collapse>
                      )}
                    </React.Fragment>
                  );
                })}
              </List>
            </Collapse>
          )}
        </React.Fragment>
      );
    });
  }, [filteredMenuItems, expandedItems, isDrawerContentVisible, handleItemClick, handleSubItemClick, handleToggleNestedExpand, activeStates]);

  // Memoize school name header separately to prevent re-renders
  const headerContent = useMemo(() => (
    <Box
      sx={{
        p: 2.3,
        display: 'flex',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 2,
        background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        borderColor: 'primary.dark',
      }}
    >
      <Box
        component="img"
        src="/logo1921.png"
        alt="School Logo"
        sx={{
          width: 'auto',
          height: 24,
          borderRadius: 2,
          mr: 2,
          objectFit: 'contain',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          border: '2px solid rgba(255,255,255,0.3)',
          bgcolor: 'rgba(255,255,255,0.1)',
        }}
      />
      <Typography
        variant="h6"
        noWrap
        sx={{
          fontWeight: 800,
          color: 'white',
          letterSpacing: '0.03em',
          textShadow: '0 2px 4px rgba(0,0,0,0.2)',
          fontSize: '1.1rem',
        }}
      >
        {schoolName}
      </Typography>
    </Box>
  ), [schoolName]);

  // Memoize drawer content to prevent unnecessary re-renders
  const drawerContent = useMemo(() => (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'white',
      }}
    >
      {headerContent}

      <List sx={{ 
        flexGrow: 1, 
        pt: 2, 
        pb: 3, 
        px: 0,
        background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(0,0,0,0.01) 100%)',
      }}>
        {menuItemsList}
      </List>
    </Box>
  ), [headerContent, menuItemsList]);

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      anchor="left"
      open={isMobile ? open : true}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

// Memoize the component with custom comparison to prevent re-renders
// Only re-render when props actually change, not when pathname changes internally
export default React.memo(Sidebar, (prevProps, nextProps) => {
  // Only re-render if these props change
  return (
    prevProps.open === nextProps.open &&
    prevProps.drawerWidth === nextProps.drawerWidth &&
    prevProps.onClose === nextProps.onClose
  );
});
